import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Clock, AlertTriangle, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';

const TakeQuizModal = ({ quiz, onClose, onComplete }) => {
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);
    const startTimeRef = useRef(new Date());

    useEffect(() => {
        fetchQuizDetails();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [quiz.id]);

    useEffect(() => {
        if (timeLeft === 0) {
            handleAutoSubmit();
        }
    }, [timeLeft]);

    const fetchQuizDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/quizzes/${quiz.id}/questions`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            const quizData = response.data;
            setQuestions(quizData.questions || []);

            if (quizData.duration) {
                setTimeLeft(quizData.duration * 60); // Convert minutes to seconds
                startTimer();
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load quiz. ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const startTimer = () => {
        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const handleOptionSelect = (questionId, option) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: option
        }));
    };

    const calculateProgress = () => {
        if (questions.length === 0) return 0;
        const answeredCount = Object.keys(answers).length;
        return Math.round((answeredCount / questions.length) * 100);
    };

    const handleAutoSubmit = () => {
        alert("Time's up! Submitting your quiz now.");
        handleSubmit();
    };

    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);

        // Stop timer
        if (timerRef.current) clearInterval(timerRef.current);

        const timeSpent = quiz.duration ? (quiz.duration * 60 - timeLeft) :
            Math.round((new Date() - startTimeRef.current) / 1000);

        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/quizzes/quiz-submissions`, {
                quizId: quiz.id,
                answers,
                timeSpent
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            onComplete();
        } catch (err) {
            console.error(err);
            // If we fail, restart timer if it was running? Use simple alert for now
            alert('Failed to submit quiz: ' + (err.response?.data?.message || err.message));
            if (timeLeft > 0) startTimer(); // Resume if failed and time remains
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading quiz...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                    <div className="text-red-600 mb-4 flex items-center gap-2">
                        <AlertTriangle size={24} />
                        <h3 className="text-lg font-bold">Error</h3>
                    </div>
                    <p className="text-gray-700 mb-6">{error}</p>
                    <button onClick={onClose} className="w-full bg-gray-200 py-2 rounded-lg hover:bg-gray-300">
                        Close
                    </button>
                </div>
            </div>
        );
    }

    // Current question
    const currentQ = questions[currentQuestionIndex];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-0 md:p-4">
            <div className="bg-white w-full h-full md:h-full max-w-[98%] md:max-w-4xl md:rounded-2xl flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">

                {/* Header */}
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{quiz.title ? quiz.title.replace('AI Quiz:', 'Smart Quiz:') : ''}</h2>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                            <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
                            {timeLeft !== null && (
                                <span className={`flex items-center gap-1 font-mono font-bold ${timeLeft < 60 ? 'text-red-600' : 'text-blue-600'}`}>
                                    <Clock size={16} /> {formatTime(timeLeft)}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (window.confirm("Are you sure you want to exit? Your progress may be lost.")) onClose();
                        }}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 h-2">
                    <div
                        className="bg-blue-600 h-2 transition-all duration-300"
                        style={{ width: `${calculateProgress()}%` }}
                    ></div>
                </div>

                {/* Question Area */}
                <div className="flex-1 overflow-y-auto p-8">
                    <div className="max-w-3xl mx-auto">
                        <div className="mb-6">
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded mb-2">
                                {currentQ.type === 'multiple_choice' ? 'Multiple Choice' :
                                    currentQ.type === 'true_false' ? 'True / False' : 'Short Answer'}
                            </span>
                            <p className="text-lg text-gray-900 font-medium">{currentQ.text}</p>
                            <p className="text-sm text-gray-500 mt-1">{currentQ.points} points</p>
                        </div>

                        <div className="space-y-3">
                            {currentQ.type === 'multiple_choice' && currentQ.options.map((opt, idx) => (
                                <label
                                    key={idx}
                                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${answers[currentQ.id] === opt
                                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                            : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name={`q-${currentQ.id}`}
                                        value={opt}
                                        checked={answers[currentQ.id] === opt}
                                        onChange={() => handleOptionSelect(currentQ.id, opt)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-3 text-gray-700">{opt}</span>
                                </label>
                            ))}

                            {currentQ.type === 'true_false' && (
                                <div className="space-y-3">
                                    {['true', 'false'].map((val) => (
                                        <label
                                            key={val}
                                            className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${answers[currentQ.id] === val
                                                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                                    : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name={`q-${currentQ.id}`}
                                                value={val}
                                                checked={answers[currentQ.id] === val}
                                                onChange={() => handleOptionSelect(currentQ.id, val)}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="ml-3 text-gray-700 capitalize">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {currentQ.type === 'short_answer' && (
                                <textarea
                                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    rows={4}
                                    placeholder="Type your answer here..."
                                    value={answers[currentQ.id] || ''}
                                    onChange={(e) => handleOptionSelect(currentQ.id, e.target.value)}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Controls */}
                <div className="p-6 border-t border-gray-200 flex justify-between items-center bg-gray-50 rounded-b-xl">
                    <button
                        onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                        disabled={currentQuestionIndex === 0}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft size={18} /> Previous
                    </button>

                    <div className="flex gap-2">
                        {currentQuestionIndex < questions.length - 1 ? (
                            <button
                                onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Next <ChevronRight size={18} />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-70"
                            >
                                {submitting ? 'Submitting...' : 'Submit Quiz'} <CheckCircle size={18} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TakeQuizModal;
